import React, { useState, useContext, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  CircularProgress,
  Avatar,
  Collapse,
  Chip,
  useTheme,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import {
  Cached as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  Code as CodeIcon,
  AccessTime as AccessTimeIcon,
  ZoomIn as ZoomInIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import AuthContext from "../../store/AuthContext";
import ViewAttendanceModal from "../../modals/ViewAttendanceModal";
import DataFilter from "./Common/DataFilter";
import { get } from "../../network/ApiController";
import ApiEndpoints from "../../network/ApiEndPoints";
import { apiErrorToast } from "../../utils/ToastUtil";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import VisitCard from "../VisitCard";

const VisitsTable = () => {
  const theme = useTheme();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const authCtx = useContext(AuthContext);
  const [query, setQuery] = useState("");
  const [apiData, setApiData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const fetchData = () => {
    get(
      `${ApiEndpoints.GET_SALES_ATTENDANCE}?page=${
        page + 1
      }&paginate=${rowsPerPage}&${query}`,
      "",
      setLoading,
      (res) => {
        if (res) {
          setApiData(res.data.data.data);
          setTotal(res.data.data.total);
          console.log("Total Count:", res.data.data.total);
        }
      },
      (error) => apiErrorToast(error)
    );
  };

  const locationCache = useRef({});

  const fetchLocationName = async (coordinates) => {
    if (!coordinates || !coordinates.includes(",")) {
      return "Location not available";
    }

    // Check cache first
    if (locationCache.current[coordinates]) {
      return locationCache.current[coordinates];
    }

    try {
      const [lat, lng] = coordinates.split(",").map(Number);
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`,
        {
          headers: {
            "User-Agent": "YourAppName/1.0 (contact@example.com)",
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch location");

      const data = await response.json();
      const locationName = data.display_name || "Location not found";

      // Cache the result
      locationCache.current[coordinates] = locationName;

      return locationName;
    } catch (error) {
      console.error("Geocoding error:", error);
      return "Error fetching location";
    }
  };

  useEffect(() => fetchData(), [page, rowsPerPage, query]);

  const refreshData = () => {
    setQuery("");
    fetchData();
  };

  const openImageModal = (imageUrl) => {
    setSelectedImage(imageUrl);
    setModalOpen(true);
  };

  const closeImageModal = () => {
    setModalOpen(false);
    setSelectedImage(null);
  };

  const handleExpandRow = (id) =>
    setExpandedRow(expandedRow === id ? null : id);

  const formatTime = (dateString) =>
    dateString
      ? new Date(dateString).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })
      : null;
  const formatDate = (dateString) =>
    dateString ? new Date(dateString).toLocaleDateString() : null;

  const getLastVisit = (row) => {
    for (let i = 8; i >= 1; i--) {
      if (row[`visit_time${i}`]) {
        return {
          time: row[`visit_time${i}`],
          location: row[`location${i}`],
          selfie: row[`selfie${i}`],
        };
      }
    }
    return null;
  };

  const getVisitCount = (row) => {
    let count = 0;
    for (let i = 1; i <= 8; i++) {
      if (row[`visit_time${i}`]) count++;
    }
    return count;
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  // Responsive table headers configuration
  const tableHeaders = [
    { id: "date", label: "Date", visible: true },
    { id: "user", label: "User", visible: true },
    { id: "lastVisit", label: "Last Visit", visible: !isSmallScreen },
    { id: "todayVisits", label: "Today's Visits", visible: !isSmallScreen },
    { id: "monthlyVisits", label: "Monthly Visits", visible: !isSmallScreen },
    { id: "details", label: "Details", visible: true },
  ];

  return (
    <Box
      sx={{
        p: isSmallScreen ? 1 : 3,
        backgroundColor: theme.palette.background.default,
        borderRadius: 4,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          gap: 2,
          flexWrap: "wrap",
          backgroundColor: theme.palette.background.paper,
          p: isSmallScreen ? 1 : 2,
          borderRadius: 3,
          boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.03)",
        }}
      >
        <Typography
          variant={isSmallScreen ? "subtitle1" : "h6"}
          fontWeight="bold"
          color="primary"
        >
          👋 Visit Records
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <DataFilter
            ifemployeeFilter={authCtx.user.role === "Admin"}
            setQuary={setQuery}
            setQuery={setQuery}
          />
          <Tooltip title="Refresh data" arrow>
            <IconButton
              onClick={refreshData}
              size="small"
              sx={{
                backgroundColor: theme.palette.primary.light,
                color: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: theme.palette.primary.main,
                  color: "#fff",
                },
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress
            size={24}
            sx={{ color: theme.palette.primary.main }}
          />
        </Box>
      ) : (
        <>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 3,
              overflowX: "auto",
            }}
          >
            <Table
              size={isSmallScreen ? "small" : "medium"}
              sx={{ minWidth: isSmallScreen ? 300 : 650 }}
            >
              <TableHead sx={{ backgroundColor: theme.palette.primary.light }}>
                <TableRow>
                  {tableHeaders.map(
                    (header) =>
                      header.visible && (
                        <TableCell
                          key={header.id}
                          sx={{
                            fontWeight: "bold",
                            color: "#000",
                            py: isSmallScreen ? 0.5 : 1.5,
                            fontSize: isSmallScreen ? "0.75rem" : "0.875rem",
                          }}
                        >
                          {header.label}
                        </TableCell>
                      )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {apiData
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => {
                    const lastVisit = getLastVisit(row);
                    const visitCount = getVisitCount(row);

                    return (
                      <React.Fragment key={row.id}>
                        <TableRow
                          hover
                          sx={{
                            backgroundColor:
                              expandedRow === row.id
                                ? theme.palette.action.hover
                                : "inherit",
                          }}
                        >
                          <TableCell>
                            <Box>
                              <Typography
                                variant="body2"
                                fontWeight={500}
                                fontSize={
                                  isSmallScreen ? "0.75rem" : "0.875rem"
                                }
                              >
                                {formatDate(row.created_at)}
                              </Typography>
                              {!isSmallScreen && (
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                >
                                  {formatTime(row.created_at)}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={`${row.user_name}`}
                              size={isSmallScreen ? "small" : "medium"}
                              sx={{
                                backgroundColor: theme.palette.grey[100],
                                color: theme.palette.text.primary,
                                fontSize: isSmallScreen
                                  ? "0.75rem"
                                  : "0.875rem",
                              }}
                            />
                          </TableCell>

                          {!isSmallScreen && (
                            <>
                              <TableCell>
                                {lastVisit ? (
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                  >
                                    <TimeIcon
                                      color="primary"
                                      fontSize="small"
                                    />
                                    <Box>
                                      <Typography variant="body2">
                                        {formatTime(lastVisit.time)}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="textSecondary"
                                      >
                                        {formatDate(lastVisit.time)}
                                      </Typography>
                                    </Box>
                                  </Box>
                                ) : (
                                  <Typography
                                    variant="body2"
                                    color="textSecondary"
                                  >
                                    No visits
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={`${visitCount} visits`}
                                  size="small"
                                  color={visitCount > 0 ? "primary" : "default"}
                                  variant={
                                    visitCount > 0 ? "filled" : "outlined"
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={`${row.this_month_visit_count}/125 visits`}
                                  size="small"
                                  color={
                                    row.this_month_visit_count >= 125
                                      ? "success"
                                      : row.this_month_visit_count >= 63
                                      ? "warning"
                                      : "error"
                                  }
                                  variant="filled"
                                  sx={{
                                    fontWeight: 500,
                                    ...(row.this_month_visit_count >= 125 && {
                                      backgroundColor:
                                        theme.palette.success.main,
                                      color: theme.palette.success.contrastText,
                                    }),
                                    ...(row.this_month_visit_count >= 63 &&
                                      row.this_month_visit_count < 125 && {
                                        backgroundColor:
                                          theme.palette.warning.main,
                                        color:
                                          theme.palette.warning.contrastText,
                                      }),
                                    ...(row.this_month_visit_count < 63 && {
                                      backgroundColor: theme.palette.error.main,
                                      color: theme.palette.error.contrastText,
                                    }),
                                  }}
                                />
                              </TableCell>
                            </>
                          )}

                          <TableCell align="center">
                            <IconButton
                              size="small"
                              onClick={() => handleExpandRow(row.id)}
                              sx={{
                                backgroundColor:
                                  expandedRow === row.id
                                    ? theme.palette.primary.main
                                    : theme.palette.grey[100],
                                color:
                                  expandedRow === row.id
                                    ? "#fff"
                                    : theme.palette.text.secondary,
                                "&:hover": {
                                  backgroundColor: theme.palette.primary.main,
                                  color: "#fff",
                                },
                              }}
                            >
                              {expandedRow === row.id ? (
                                <ExpandLessIcon />
                              ) : (
                                <ExpandMoreIcon />
                              )}
                            </IconButton>
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            colSpan={
                              tableHeaders.filter((h) => h.visible).length
                            }
                            sx={{ p: 0 }}
                          >
                            <Collapse
                              in={expandedRow === row.id}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box
                                p={isSmallScreen ? 1 : 2}
                                bgcolor={theme.palette.background.paper}
                              >
                                <Typography
                                  variant="subtitle2"
                                  gutterBottom
                                  sx={{
                                    color: theme.palette.primary.main,
                                    fontSize: isSmallScreen
                                      ? "0.875rem"
                                      : "1rem",
                                  }}
                                >
                                  🕒 Visit Details
                                </Typography>
                                <Box
                                  display="grid"
                                  gridTemplateColumns={
                                    isSmallScreen
                                      ? "1fr"
                                      : isMediumScreen
                                      ? "repeat(auto-fill, minmax(250px, 1fr))"
                                      : "repeat(auto-fill, minmax(280px, 1fr))"
                                  }
                                  gap={2}
                                >
                                  {[1, 2, 3, 4, 5, 6, 7, 8].map(
                                    (i) =>
                                      (row[`visit_time${i}`] ||
                                        row[`selfie${i}`]) && (
                                        <VisitCard
                                          key={i}
                                          visitNumber={i}
                                          row={row}
                                          onDeleteSuccess={fetchData}
                                          openImageModal={openImageModal}
                                          fetchLocationName={fetchLocationName}
                                          isSmallScreen={isSmallScreen}
                                        />
                                      )
                                  )}
                                </Box>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={
              isSmallScreen ? [5, 10, 25] : [5, 10, 25, 30, 35, 40, 70, total]
            }
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              mt: 2,
              "& .MuiTablePagination-toolbar": {
                flexWrap: "wrap",
                justifyContent: "center",
              },
              "& .MuiTablePagination-actions": {
                display: isSmallScreen ? "none" : "flex",
              },
            }}
            labelRowsPerPage={isSmallScreen ? "" : "Rows per page:"}
          />
        </>
      )}

      <ViewAttendanceModal
        open={modalOpen}
        closeImageModal={closeImageModal}
        selectedImage={selectedImage}
      />
    </Box>
  );
};

export default VisitsTable;
